import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { DEFAULT_MARKET, MARKET_CODES, MARKET_CONFIGS, MarketCode, MarketConfig } from './marketConfig'

const MARKET_COOKIE = 'pixie_market'
const MARKET_LS_KEY = 'pixie_market'
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60 // 1 year in seconds

interface MarketContextValue {
  market: MarketConfig
  marketCode: MarketCode
  setMarket: (code: MarketCode) => void
}

const MarketContext = createContext<MarketContextValue | null>(null)

function readMarketCookie(): MarketCode | null {
  if (typeof document === 'undefined') return null

  const match = document.cookie.match(new RegExp(`(?:^|; )${MARKET_COOKIE}=([^;]*)`))

  if (!match) return null

  const value = match[1] as MarketCode

  return MARKET_CODES.includes(value) ? value : null
}

function writeMarketCookie(code: MarketCode) {
  if (typeof document === 'undefined') return

  const isSecure = window.location.protocol === 'https:'
  const parts = [
    `${MARKET_COOKIE}=${code}`,
    'path=/',
    `max-age=${COOKIE_MAX_AGE}`,
    'SameSite=Lax',
  ]

  if (isSecure) parts.push('Secure')

  document.cookie = parts.join('; ')
}

function readMarketLocalStorage(): MarketCode | null {
  if (typeof window === 'undefined') return null

  try {
    const value = localStorage.getItem(MARKET_LS_KEY) as MarketCode | null

    return value && MARKET_CODES.includes(value) ? value : null
  } catch {
    return null
  }
}

function writeMarketLocalStorage(code: MarketCode) {
  if (typeof window === 'undefined') return

  try {
    localStorage.setItem(MARKET_LS_KEY, code)
  } catch {
    // localStorage unavailable
  }
}

function detectMarketFromBrowser(): MarketCode | null {
  if (typeof navigator === 'undefined') return null

  const lang = navigator.language || ''
  const lower = lang.toLowerCase()

  if (lower.startsWith('en-au')) return 'AU'
  if (lower.startsWith('en-us')) return 'US'
  if (lower.startsWith('en-gb')) return 'GB'

  return null
}

function resolveInitialMarket(): MarketCode {
  return readMarketCookie()
    ?? readMarketLocalStorage()
    ?? detectMarketFromBrowser()
    ?? DEFAULT_MARKET
}

interface MarketProviderProps {
  children: ReactNode
}

export function MarketProvider({ children }: MarketProviderProps) {
  const [marketCode, setMarketCode] = useState<MarketCode>(DEFAULT_MARKET)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMarketCode(resolveInitialMarket())
    setMounted(true)
  }, [])

  const setMarket = useCallback((code: MarketCode) => {
    setMarketCode(code)
    writeMarketCookie(code)
    writeMarketLocalStorage(code)
  }, [])

  // Persist on initial detection (only once after mount)
  useEffect(() => {
    if (!mounted) return

    // Only write if no cookie exists yet (first visit detection)
    if (!readMarketCookie()) {
      writeMarketCookie(marketCode)
      writeMarketLocalStorage(marketCode)
    }
  }, [mounted, marketCode])

  const market = MARKET_CONFIGS[marketCode]

  const value = useMemo<MarketContextValue>(() => ({
    market,
    marketCode,
    setMarket,
  }), [market, marketCode, setMarket])

  return <MarketContext.Provider value={value}>{children}</MarketContext.Provider>
}

export function useMarket(): MarketContextValue {
  const context = useContext(MarketContext)

  if (!context) {
    throw new Error('useMarket must be used within a MarketProvider')
  }

  return context
}
